import { settings } from "app";
import { tenants } from "utils/tenants";
import { LEAVES_REQUESTS, LeavesServicePath } from "utils/constants";
import {
  setTokens,
  getAccessToken,
  getRefreshToken,
  removeTokens,
  setPermissions,
  getPermissions,
  removeUser,
  authCallbackPath,
  selectProfilePath,
  setUser,
  getUser,
  setEmployeeProfiles,
  setProfileSelectionPending,
  clearProfileSelectionPending,
  isProfileSelectionPending,
} from "./securityUtils";
import { accountRequests } from "api/accountApi";

export const fetchPermissions = () => {
  const request = new Request(`${settings.authorizationApi}/security/permissions`, {
    headers: new Headers({
      "X-Session-Id": `${getAccessToken() === "null" ? "toni" : getAccessToken()}`,
    }),
  });
  return fetch(request)
    .then(res => res.json())
    .then(data => {
      setPermissions(JSON.stringify(data));
    });
};

export const fetchUserData = () => {
  const request = new Request(`${settings.authorizationApi}/users/security`, {
    headers: new Headers({
      "X-Session-Id": `${getAccessToken() === "null" ? "toni" : getAccessToken()}`,
    }),
  });
  return fetch(request)
    .then(res => res.json())
    .then(data => {
      data.tpInfo = data.tpInfo || data.customerInfo || {};
      data.tpInfo.userId = data.userData.userId;
      data.tpInfo.employeeId = data.userData.employeeId;

      setUser(JSON.stringify(data.tpInfo));
    });
};

// Position-based employee profiles (the switcher and the picker read the cached copy). A failure here must not
// block the login, so it degrades to "feature off" for this session.
export const fetchProfiles = () => {
  const request = new Request(`${settings.ldapLoginAPI}/${accountRequests.getProfiles()}`, {
    headers: new Headers({
      "X-Session-Id": getAccessToken(),
    }),
  });
  return fetch(request)
    .then(res => {
      if (res.ok) return res.json();
      console.error("Failed to load the employee profiles", res.status);
      return null;
    })
    .then(data => setEmployeeProfiles(data ?? { enabled: false, current: null, profiles: [] }))
    .catch(error => {
      console.error("Failed to load the employee profiles", error);
      setEmployeeProfiles({ enabled: false, current: null, profiles: [] });
    });
};

// Stores a (new) session id and reloads everything derived from it: permissions, identity and employee profiles.
export const applySessionAndLoadUser = async sessionId => {
  setTokens(sessionId);
  if (settings.usesSeparatePermissions) {
    await fetchPermissions();
  } else {
    setPermissions(JSON.stringify({ isAdmin: true }));
  }
  await fetchUserData();
  await fetchProfiles();
  clearProfileSelectionPending();
};

const authProvider = {
  // login: ({ username, password, reCaptchaToken, changePasswordCallback, thirdParty }) => {
  //   let body = {};
  //   body[userNameString] = username;
  //   body[passwordString] = password;
  //   body[reCaptchaString] = reCaptchaToken;
  login: ({ thirdParty, username, password, altchaPayload, accountType }) => {
    if (thirdParty) {
      const request = new Request(
        `${settings.authorizationApi}/${settings.loginEndpoint || "login"}?clientId=${
          settings.authClientId
        }&partyname=${thirdParty}&redirecturl=${window.location.origin}/${authCallbackPath}`,
        { method: "GET" },
      );
      return fetch(request)
        .then(response => {
          if (response.status < 200 || response.status >= 300) return Promise.reject();
          return response.json();
        })
        .then(async auth => {
          window.location.href = auth.authenticationLocation;
        })
        .catch(() => Promise.reject());
    }

    // Password login (email + password + Altcha)
    const body = {
      username: username, // TODO: Fix the settings, because currently username is email, but it should be consistent
      [settings.passwordString]: password,
      reCaptchaToken: altchaPayload,
      accountType,
    };

    const request = new Request(`${settings.ldapLoginAPI}/${accountRequests.ldapLogin()}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: new Headers({ "Content-Type": "application/json" }),
    });
    return fetch(request)
      .then(response => {
        if (response.status < 200 || response.status >= 300) return Promise.reject();
        return response.json();
      })
      .then(async auth => {
        if (auth.requiresProfileSelection) {
          // The session has no roles yet - the employee must pick a node/position profile first.
          setTokens(auth.sessionId);
          setProfileSelectionPending();
          return Promise.resolve({ ...auth, redirectTo: `/${selectProfilePath}` });
        }

        await applySessionAndLoadUser(auth.sessionId);

        // SU lands on the leave-requests page after login, unless the user was
        // deep-linking to a specific page before being bounced to /login.
        const pendingRedirect = sessionStorage.getItem("redirectAfterLogin");
        if (!pendingRedirect && settings.tenant === tenants.SU) {
          return Promise.resolve({ ...auth, redirectTo: `/${LeavesServicePath}${LEAVES_REQUESTS}` });
        }

        return Promise.resolve(auth);
      })
      .catch(() => Promise.reject());
  },

  checkError: params => {
    if (
      params.ok === false &&
      (params.url.includes("authentication/refresh") ||
        params.url.includes(`authentication/${settings.loginEndpoint || "login"}`))
    ) {
      return Promise.reject();
    }

    return Promise.resolve();
  },

  checkAuth: () => {
    const url = window.location.pathname;
    if (url === `/${authCallbackPath}` || url === `/${selectProfilePath}`) {
      return Promise.resolve();
    }
    const hasToken = getAccessToken() || getRefreshToken();
    if (!hasToken) {
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      if (
        currentPath &&
        currentPath !== "/login" &&
        currentPath !== "/" &&
        !currentPath.includes(`/${authCallbackPath}`) &&
        !currentPath.includes("/auth-callback")
      ) {
        sessionStorage.setItem("redirectAfterLogin", currentPath);
      }
      return Promise.reject({ redirectTo: "/login" });
    }
    if (isProfileSelectionPending()) {
      // A pending session grants nothing until a profile is chosen - keep the user on the picker.
      return Promise.reject({ redirectTo: `/${selectProfilePath}`, message: false });
    }
    return Promise.resolve();
  },

  logout: () => {
    // react-admin calls logout() whenever checkAuth rejects. While a profile selection is pending the session is
    // valid but role-less - keep it so the picker can still use it, and just send the user there.
    if (isProfileSelectionPending() && getAccessToken()) {
      return Promise.resolve(`/${selectProfilePath}`);
    }

    removeTokens();
    removeUser();

    return Promise.resolve();
  },

  getIdentity: () => Promise.resolve(getUser()),

  getPermissions: () => Promise.resolve(getPermissions()),
};

export default authProvider;
